import { Chatbox, Session } from 'talkjs/all';
import { createTalkSession, createTalkUser } from '@rsos/utils/talkJS';

export interface User {
  id?: string | number | undefined;
  name?: string | undefined;
  email?: string | null | undefined;
  custom?: object | undefined;
  role?: string | undefined;
}

// Keep the DOM and the API instances in the module scope
let talkSession: Session | null = null;
let sessionPromise: Promise<Session> | null = null;
const sessionContainers = new Map<string, HTMLDivElement>();
const talkChats = new Map<string, Chatbox>();
let showroom: HTMLDivElement | null = null;

export const initializeSession = async (user = {} as User) => {
  if (talkSession) return talkSession;
  if (sessionPromise) return await sessionPromise;

  sessionPromise = (async () => {
    const session = await createTalkSession(user);
    session.user(createTalkUser(user));
    talkSession = session;
    return session;
  })();

  return await sessionPromise;
};

interface GetThemedChatInterface {
  theme: string;
  user?: User;
}

export const getThemedChat = async ({
  theme,
  user,
}: GetThemedChatInterface) => {
  const session = await initializeSession(user);

  if (talkChats.has(theme)) {
    return {
      container: sessionContainers.get(theme)!,
      chat: talkChats.get(theme)!,
    };
  }

  if (!showroom) {
    showroom = document.createElement('div');
    showroom.id = 'talkjs-showroom';
    document.body.appendChild(showroom);
  }

  const container = document.createElement('div');

  Object.assign(container.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '0',
    height: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
  });

  showroom.appendChild(container);

  const chat = session.createChatbox({
    theme: theme,
    showChatHeader: false,
    messageField: { visible: true, placeholder: 'Type Message' },
    messageFilter: { custom: { tf911ConversationID: '!exists' } },
  });
  await chat.mount(container);

  sessionContainers.set(theme, container);
  talkChats.set(theme, chat);

  return { container, chat };
};

export const getTalkInstances = (theme: string) => {
  return {
    session: talkSession,
    chat: talkChats.get(theme) || null,
    container: sessionContainers.get(theme) || null,
  };
};
